import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Label } from "./styles";
import LabelWithHelp from "./LabelWithHelp";
import SearchTextField from "./SearchTextField";
import CreatableSelect from "react-select/creatable";
import makeAnimated from "react-select/animated";
import {
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupsIcon from "@mui/icons-material/Groups";

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;

  /* Keep the 4 metacategories on the same row for typical form widths.
     On smaller screens we allow wrapping so inputs stay usable. */
  @media (max-width: 980px) {
    flex-wrap: wrap;
  }
`;
const FormCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const animatedComponents = makeAnimated();

const META_LABELS = {
  actors: "Acteur(s)",
  rp: "Catégorie(s) RP",
  hrp: "Catégorie(s) HRP",
  localization: "Localisation(s)",
};

function SearchableCheckboxColumn({ label, options, selected, onChange, onCreateOption, metaName }) {
  const [filter, setFilter] = useState("");
  const [newName, setNewName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, filter]);

  const handleAdd = () => {
    const name = newName.trim();
    if (!name || !onCreateOption) return;
    setConfirmOpen(true);
  };

  const handleConfirmAdd = () => {
    const name = newName.trim();
    if (!name || !onCreateOption) return;
    onCreateOption(name);
    setNewName("");
    setConfirmOpen(false);
  };

  const handleCancelAdd = () => {
    setConfirmOpen(false);
  };

  return (
    <FormCol>
      <Label>{label}</Label>
      <SearchTextField
        placeholder="Rechercher…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <Box
        sx={{
          maxHeight: 320,
          overflowY: "auto",
          mt: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {filtered.map((cat) => (
          <FormControlLabel
            key={cat.value}
            sx={{
              margin: 0,
              marginBottom: -1,
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
                lineHeight: 1.2,
              },
            }}
            control={
              <Checkbox
                size="small"
                checked={selected.includes(cat.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selected, cat.value]);
                  } else {
                    onChange(selected.filter((id) => id !== cat.value));
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {cat.isOfficial ? (
                  <VerifiedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                ) : (
                  <GroupsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                )}
                <span>{cat.label}</span>
              </Box>
            }
          />
        ))}
      </Box>
      {onCreateOption && (
        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Nouvelle catégorie…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())}
            sx={{ flex: "1 1 120px", minWidth: 120 }}
          />
          <Button type="button" variant="outlined" size="small" onClick={handleAdd}>
            Ajouter
          </Button>
        </Box>
      )}
      <Dialog open={confirmOpen} onClose={handleCancelAdd} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmer l'ajout</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Ajouter la catégorie <strong>{newName.trim()}</strong> dans la métacatégorie{" "}
            <strong>{metaName}</strong> ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAdd}>Annuler</Button>
          <Button variant="contained" onClick={handleConfirmAdd}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </FormCol>
  );
}

/**
 * - variant="event": shows "RP Officiel ou Non Officiel?" radio + metacategories + custom.
 * - variant="period": no official radio; same metacategories + custom.
 * - value: { actors: [], rp: [], hrp: [], localization: [], custom: [], officialStatus: '' }
 * - groupedCategories: per-meta official/community arrays + customOptions + officialCategories
 */
const CategorySelection = ({
  variant,
  value,
  onChange,
  groupedCategories,
  onCreateCustomCategory,
  onCreateCommunityCategory,
  error,
}) => {
  const {
    actorsCategories = [],
    rpCategories = [],
    hrpCategories = [],
    localizationCategories = [],
    actorsCommunityCategories = [],
    rpCommunityCategories = [],
    hrpCommunityCategories = [],
    localizationCommunityCategories = [],
    customOptions = [],
    officialCategories = [],
  } = groupedCategories;

  const mergedMetaConfig = [
    { key: "actors", options: actorsCategories },
    { key: "rp", options: rpCategories },
    { key: "hrp", options: hrpCategories },
    { key: "localization", options: localizationCategories },
  ].map((item) => {
    const commMap = {
      actors: actorsCommunityCategories,
      rp: rpCommunityCategories,
      hrp: hrpCommunityCategories,
      localization: localizationCommunityCategories,
    };
    const metaNameMap = {
      actors: "Acteur",
      rp: "RP",
      hrp: "HRP",
      localization: "Localisation",
    };
    return {
      key: item.key,
      metaName: metaNameMap[item.key],
      options: [...item.options, ...(commMap[item.key] || [])],
    };
  });

  return (
    <>
      {variant === "event" && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 255 }}>
              <LabelWithHelp
                label="RP Officiel ou Non Officiel ?"
                info="Le RP officiel est le RP produit par l'administration et la modération du serveur (News, annonces, ouverture de map, etc.). Le RP non officiel est celui produit par les joueurs."
              />
            </Box>
            <RadioGroup
              row
              required
              value={value.officialStatus}
              sx={{
                marginLeft: "4px",
                marginTop: 0,
                "& .MuiFormControlLabel-root": { marginRight: 2.5 },
              }}
              onChange={(e) => onChange("officialStatus", e.target.value)}
            >
              {officialCategories.map((cat) => (
                <FormControlLabel
                  key={cat.value}
                  value={String(cat.value)}
                  control={<Radio size="small" />}
                  label={cat.label}
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.95rem" } }}
                />
              ))}
            </RadioGroup>
          </Box>
        </>
      )}

      <Label style={{ marginBottom: 8 }}>Catégories :</Label>

      <FormRow>
        {mergedMetaConfig.map(({ key, options, metaName }) => (
          <SearchableCheckboxColumn
            key={`merged-${key}`}
            label={META_LABELS[key]}
            options={options}
            selected={value[key] || []}
            onChange={(selected) => onChange(key, selected)}
            metaName={metaName}
            onCreateOption={
              onCreateCommunityCategory ? (name) => onCreateCommunityCategory(metaName, name) : undefined
            }
          />
        ))}
      </FormRow>

      <Label>Catégorie(s) custom :</Label>
      <CreatableSelect
        classNamePrefix="react-select"
        value={value.custom || []}
        onCreateOption={onCreateCustomCategory}
        onChange={(selectedOptions) => onChange("custom", selectedOptions || [])}
        closeMenuOnSelect={false}
        components={animatedComponents}
        isMulti
        options={customOptions}
      />
      {error && <span style={{ color: "red", fontSize: "0.875rem" }}>{error}</span>}
    </>
  );
};

export default CategorySelection;
